import {Grid, Paper, Title} from '@mantine/core';
import {FC, useCallback} from 'react';
import {SubmitHandler} from 'react-hook-form';
import {useTypedParams} from 'react-router-typesafe-routes/dom';
import {JobsService} from '../../api/generated/JobsService';
import {AsyncStateContainer} from '../../components/async-state-container';
import {createForm} from '../../forms';
import {useNotification} from '../../hooks/use-notifications';
import {ROUTES} from '../../routes';
import {useJobStore} from '../job-store';
import {jobPartKitSchema, JobPartKitSchema} from '../schemas';
import classes from './job-evaluation.module.css';
import {useAsync} from 'react-use';
import {JobTypes} from '../../api/generated/enums.ts';

const Form = createForm<JobPartKitSchema>();

export const JobEvaluationPartKit: FC = () => {
  const notifications = useNotification();
  const {id} = useTypedParams(ROUTES.ADMIN.JOBS.DETAIL);

  const {job, isLoading, isJobReadonly, fetchJob} = useJobStore((state) => ({
    job: state.job ?? undefined,
    isLoading: state.isJobLoading,
    isJobReadonly: state.isJobReadonly,
    fetchJob: state.fetchJob,
  }));

  const onSubmit = useCallback<SubmitHandler<JobPartKitSchema>>(
    async (value) => {
      const response = await JobsService.updatePartKit({
        jobId: id,
        body: value,
      });

      if (response.hasErrors) {
        return response;
      }

      notifications.success('Part kit selected');
      await fetchJob(id);
    },
    [fetchJob, id, notifications],
  );

  return (
    <div>
      <Title fz='sm' className={classes.title}>
        Available Part Kits
      </Title>
      <Paper mih={95} shadow='sm' radius='0 0 6px 6px'>
        <AsyncStateContainer loading={!job && isLoading}>
          {job && (
            <Form
              onSubmit={onSubmit}
              defaultValues={job}
              schema={jobPartKitSchema}
            >
              {({formState: {isSubmitting}}) => (
                <>
                  <Grid align='center' justify='flex-end' gutter='xs' p={16}>
                    <Grid.Col span={10} p={0}>
                      <PartKitField
                        readOnly={isJobReadonly}
                        jobType={job.jobType as JobTypes}
                      />
                    </Grid.Col>
                    <Grid.Col span={2}>
                      <Form.Submit
                        loading={isSubmitting}
                        disabled={isJobReadonly}
                        w='100%'
                        mt='md'
                      >
                        Update
                      </Form.Submit>
                    </Grid.Col>
                  </Grid>
                </>
              )}
            </Form>
          )}
        </AsyncStateContainer>
      </Paper>
    </div>
  );
};

type PartKitFieldProps = {
  readOnly?: boolean;
  jobType: JobTypes;
};
const PartKitField: FC<PartKitFieldProps> = ({readOnly, jobType}) => {
  const {id} = useTypedParams(ROUTES.ADMIN.JOBS.DETAIL);

  const fetchPartKitOptions = useAsync(async () => {
    if (jobType === JobTypes['Non-M&M']) return;

    const {data} = await JobsService.getPartKitOptionsByJobId({jobId: id});
    return data;
  }, [id, jobType]);

  const partKitOptions = fetchPartKitOptions.value ?? [];

  if (jobType === JobTypes['Non-M&M']) {
    return (
      <Form.TextInput
        name={'partKitNumber'}
        label={'Part Kit'}
        readOnly={readOnly}
      />
    );
  }

  return (
    <Form.Select
      clearable
      label='Part Kit'
      name='partKitNumber'
      data={partKitOptions}
      readOnly={readOnly}
    />
  );
};